import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { btn } from "../components/kit";

export default function Login() {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [demo, setDemo] = useState({ demo_mode: false, users: [] });

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);
  useEffect(() => {
    api.get("/auth/demo-users").then((r) => setDemo(r.data)).catch(() => {});
  }, []);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const demoLogin = async (id) => {
    try {
      const r = await api.post("/auth/demo-login", { user_id: id });
      signIn(r.data);
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 text-white" data-testid="login-hero">
        <img src="/brand/login_sign.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-[50%_30%] scale-105 login-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ink)] via-[color:var(--ink)]/55 to-[color:var(--ink)]/10" />
        <img src="/brand/dcbc_mark_white.png" alt="Dominion City Brewing Co." className="relative w-20 drop-shadow" data-testid="login-mark" />
        <div className="relative max-w-md fade-up">
          <p className="eyebrow text-[color:var(--dcbc-blue)] mb-4">Staff only · Ottawa</p>
          <h1 className="text-5xl font-semibold leading-tight">Workplace</h1>
          <p className="mt-5 text-lg text-white/80">Your all-in-one DCBC training and employee dashboard: training, policies, documents, reviews, safety and the people you work with, from the brew deck to Stubby's.</p>
          <p className="mt-10 text-xs text-white/50">Dominion City Brewing Co. · Internal use</p>
        </div>
      </div>
      <div className="relative h-[32vh] min-h-[200px] overflow-hidden lg:hidden" data-testid="login-hero-mobile">
        <img src="/brand/login_sign.jpg" alt="Dominion City Brewing Co. sign" className="absolute inset-0 h-full w-full object-cover object-[50%_25%]" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />
      </div>
      <div className="flex flex-col justify-start lg:justify-center p-6 pt-2 sm:p-12">
        <div className="mx-auto w-full max-w-md fade-up">
          <div className="mb-8 lg:hidden"><p className="eyebrow text-[color:var(--dcbc-red)]">Staff only · Ottawa</p><p className="mt-1 text-2xl font-semibold">Workplace</p></div>
          <h2 className="text-3xl font-semibold">Sign in</h2>
          <p className="mt-2 text-muted-foreground">This portal is invitation-only. Use the exact Google account your invitation was sent to.</p>
          <button onClick={google} data-testid="login-google-button" className={`${btn.dark} w-full mt-8 text-base`}>
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="#fff" d="M21.35 11.1H12v2.98h5.35c-.23 1.4-1.66 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.96S8.78 6.26 12 6.26c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.69 3.72 14.53 2.8 12 2.8 6.92 2.8 2.8 6.92 2.8 12s4.12 9.2 9.2 9.2c5.31 0 8.83-3.73 8.83-8.99 0-.6-.07-1.06-.15-1.51z" /></svg>
            Sign in with Google
          </button>
          <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" /> No public sign-up. If you weren't invited, please speak to your manager.
          </p>

          {demo.demo_mode && (
            <div className="mt-10 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-5" data-testid="demo-switcher">
              <p className="flex items-center gap-2 font-semibold text-amber-900"><FlaskConical className="h-4 w-4" /> Demo login — sample people only</p>
              <p className="text-xs text-amber-800 mt-1 mb-4">Works only while demo mode is on. These are fictional people with sample data.</p>
              <div className="grid gap-2">
                {demo.users.map((u) => (
                  <button key={u.id} onClick={() => demoLogin(u.id)} data-testid={`demo-login-${u.name.split(" ")[0].toLowerCase()}`}
                    className="tap flex items-center justify-between rounded-lg border border-amber-200 bg-white px-4 text-left hover:border-amber-500 transition-colors">
                    <span className="font-semibold">{u.name}</span>
                    <span className="text-xs text-muted-foreground text-right">{u.demo_label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
